import { useCallback, useEffect, useMemo, useState } from "react";
import axiosInstance from "../../config/axiosApi/axiosApi.jsx";
import APIs from "../../Constants/apis.js";
import showErrorToast, { showSuccessToast } from "../../components/CustomComponents/toast/toast.js";
import FormInput from "../../components/CustomComponents/Input/formInput.jsx";
import ToggleSwitch from "../../components/CustomComponents/Switchery/switchery.jsx";
import CountryCitySelector from "../../components/CustomComponents/DropdownSelections/CountryCitySelector.jsx";
import Select from "react-select";
import useDarkMode from "../../hooks/useDarkMode.js";
import useThemeColor from "../../hooks/useThemeColor.js";
import DropDownStyles, { DropDownStylesDark } from "../staff/dropdownStyles.js";
import {
  buildRiderUpsertPayload,
  defaultRiderForm,
  mapRiderToForm,
  toImageUrl,
} from "./riderFormUtils.js";

const EMPLOYMENT_OPTIONS = [
  { value: "Freelancer", label: "Freelancer" },
  { value: "Salaried", label: "Salaried" },
];

const HOURS_SHIFT_OPTIONS = [
  { value: 8, label: "8 hours" },
  { value: 10, label: "10 hours" },
  { value: 12, label: "12 hours" },
];

const STAR_LEVEL_OPTIONS = [
  { value: 1, label: "1 star" },
  { value: 2, label: "2 stars" },
  { value: 3, label: "3 stars" },
  { value: 4, label: "4 stars" },
  { value: 5, label: "5 stars" },
];

const APPROVAL_OPTIONS = [
  { value: "Approved", label: "Approved" },
  { value: "Pending", label: "Pending" },
  { value: "Rejected", label: "Rejected" },
  { value: "Suspended", label: "Suspended" },
];

const DOCUMENT_FIELDS = [
  { key: "profileImagePath", documentType: "profile", label: "Profile picture" },
  { key: "cnicFrontImagePath", documentType: "cnic-front", label: "CNIC front" },
  { key: "cnicBackImagePath", documentType: "cnic-back", label: "CNIC back" },
  { key: "vehicleCopyImagePath", documentType: "vehicle-copy", label: "Vehicle copy" },
  { key: "drivingLicenseImagePath", documentType: "driving-license", label: "Driving licence" },
];

const MarketplaceRiderForm = ({
  rider = null,
  onSubmit,
  onCancel,
  submitting = false,
}) => {
  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();
  const dropdownStyles = isDarkMode ? DropDownStylesDark : DropDownStyles;
  const [form, setForm] = useState(defaultRiderForm);
  const [uploading, setUploading] = useState(null);
  const [zoneOptions, setZoneOptions] = useState([]);
  const [selectedResidenceZone, setSelectedResidenceZone] = useState(null);
  const isEdit = !!(rider && (rider.marketplaceRiderId || rider.marketplaceRider_ID));

  const selectStyles = useMemo(
    () => ({
      ...dropdownStyles,
      control: (provided, state) => ({
        ...dropdownStyles.control(provided, state),
        minHeight: "44px",
        height: "44px",
        borderRadius: "10px",
        background: isDarkMode ? "#2d3436" : "#F9F9F9",
        border: isDarkMode ? "1px solid #404040" : "1px solid #D9D9D9",
        boxShadow: "none",
      }),
      menuPortal: (provided) => ({
        ...provided,
        zIndex: 10150,
      }),
      menu: (provided) => ({
        ...dropdownStyles.menu(provided),
        zIndex: 10150,
      }),
    }),
    [dropdownStyles, isDarkMode]
  );

  const sectionCardStyle = useMemo(
    () => ({
      background: isDarkMode ? "rgba(45, 52, 54, 0.45)" : "#fff",
      border: isDarkMode ? "1px solid rgba(255,255,255,0.08)" : "1px solid #eceff1",
      borderRadius: "12px",
      boxShadow: isDarkMode ? "none" : "0 1px 4px rgba(0,0,0,0.04)",
    }),
    [isDarkMode]
  );

  const sectionAccent = {
    width: "4px",
    height: "18px",
    borderRadius: "4px",
    background: themeColor,
    flexShrink: 0,
  };

  const docCardStyle = useMemo(
    () => ({
      borderRadius: "10px",
      border: isDarkMode ? "1px solid #404040" : "1px solid #D9D9D9",
      background: isDarkMode ? "#2d3436" : "#F9F9F9",
      minHeight: "100%",
    }),
    [isDarkMode]
  );

  const loadZones = useCallback(async (countryCode, serviceCity) => {
    if (!countryCode || !serviceCity) {
      setZoneOptions([]);
      return;
    }
    try {
      const response = await axiosInstance.get(APIs.GET_MARKETPLACE_ZONES, {
        params: {
          CountryCode: countryCode,
          ServiceCity: serviceCity,
          IsActive: true,
          PageNumber: 1,
          PageSize: 200,
        },
      });
      if (response.data?.status) {
        const rows = Array.isArray(response.data.data) ? response.data.data : [];
        setZoneOptions(
          rows.map((z) => ({
            value: z.zoneId || z.Zone_ID,
            label: z.zoneName || z.ZoneName,
          }))
        );
      } else {
        setZoneOptions([]);
      }
    } catch {
      setZoneOptions([]);
    }
  }, []);

  useEffect(() => {
    if (rider) {
      const mapped = mapRiderToForm(rider);
      setForm(mapped);
      loadZones(mapped.serviceCountryCode, mapped.serviceCity);
    } else {
      setForm(defaultRiderForm);
      setSelectedResidenceZone(null);
      setZoneOptions([]);
    }
  }, [rider, loadZones]);

  useEffect(() => {
    loadZones(form.serviceCountryCode, form.serviceCity);
  }, [form.serviceCountryCode, form.serviceCity, loadZones]);

  useEffect(() => {
    if (!form.residenceZoneId) {
      setSelectedResidenceZone(null);
      return;
    }
    const matched = zoneOptions.find((o) => o.value === form.residenceZoneId);
    if (matched) {
      setSelectedResidenceZone(matched);
    } else {
      const fromZones = (rider?.zones || []).find(
        (z) => (z.zoneId || z.Zone_ID) === form.residenceZoneId
      );
      setSelectedResidenceZone({
        value: form.residenceZoneId,
        label:
          rider?.residenceZoneName ||
          fromZones?.zoneName ||
          fromZones?.ZoneName ||
          `Zone ${form.residenceZoneId}`,
      });
    }
  }, [zoneOptions, form.residenceZoneId, rider]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleImageUpload = async (e, documentType, fieldName) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const data = new FormData();
    data.append("ImageFile", file);
    try {
      setUploading(documentType);
      const response = await axiosInstance.post(
        `${APIs.POST_UPLOAD_MARKETPLACE_RIDER_DOCUMENT}?DocumentType=${documentType}`,
        data,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      if (response.data?.status) {
        const filePath = response.data?.data?.filePath || "";
        setForm((prev) => ({ ...prev, [fieldName]: filePath }));
        showSuccessToast("Image uploaded.");
      } else {
        showErrorToast(response.data?.message || "Upload failed.");
      }
    } catch {
      showErrorToast("Upload failed.");
    } finally {
      setUploading(null);
      e.target.value = "";
    }
  };

  const handleSave = () => {
    if (!form.firstName.trim() || !form.lastName.trim() || !form.email.trim() || !form.phone.trim()) {
      showErrorToast("First name, last name, email, and phone are required.");
      return;
    }
    if (!isEdit && !form.password.trim()) {
      showErrorToast("Password is required for new riders.");
      return;
    }
    if (!form.serviceCity.trim()) {
      showErrorToast("Service city is required");
      return;
    }
    onSubmit(buildRiderUpsertPayload(form, selectedResidenceZone));
  };

  const employmentOption = EMPLOYMENT_OPTIONS.find((o) => o.value === form.employmentType) || EMPLOYMENT_OPTIONS[0];
  const hoursOption = HOURS_SHIFT_OPTIONS.find((o) => o.value === Number(form.hoursShift)) || HOURS_SHIFT_OPTIONS[0];
  const starOption = STAR_LEVEL_OPTIONS.find((o) => o.value === Number(form.starLevel)) || STAR_LEVEL_OPTIONS[0];
  const approvalOption = APPROVAL_OPTIONS.find((o) => o.value === form.approvalStatus) || APPROVAL_OPTIONS[1];
  const isSalaried = form.employmentType === "Salaried";
  const busy = submitting || !!uploading;

  const renderSectionTitle = (title) => (
    <div className="d-flex align-items-center gap-2 mb-3">
      <span style={sectionAccent} />
      <h5 className="mb-0" style={{ fontSize: "1rem", fontWeight: 600 }}>{title}</h5>
    </div>
  );

  const primaryBtnStyle = {
    backgroundColor: themeColor,
    background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
    border: "none",
    color: "white",
    borderRadius: "8px",
    padding: "10px 20px",
    fontSize: "14px",
    fontWeight: "600",
    boxShadow: `0 2px 8px ${themeColor}40`,
    minWidth: "140px",
  };

  const cancelBtnStyle = {
    backgroundColor: isDarkMode ? "rgba(255,255,255,0.08)" : "#C4C4C4",
    border: "none",
    color: isDarkMode ? "#fff" : "#000",
    borderRadius: "8px",
    padding: "10px 20px",
    fontSize: "14px",
    fontWeight: "600",
    minWidth: "110px",
  };

  return (
    <div>
      <div className="p-3 mb-3" style={sectionCardStyle}>
        {renderSectionTitle("Basic info")}
        <div className="row g-3">
          <div className="col-md-6">
            <FormInput label="First name" type="text" name="firstName" value={form.firstName} onchange={handleChange} placeHolder="First name" />
          </div>
          <div className="col-md-6">
            <FormInput label="Last name" type="text" name="lastName" value={form.lastName} onchange={handleChange} placeHolder="Last name" />
          </div>
          <div className="col-md-6">
            <FormInput label="Email" type="email" name="email" value={form.email} onchange={handleChange} placeHolder="email@example.com" />
          </div>
          <div className="col-md-6">
            <FormInput label="Phone" type="text" name="phone" value={form.phone} onchange={handleChange} placeHolder="Phone number" />
          </div>
          <div className="col-md-6">
            <FormInput
              label={isEdit ? "Password (leave blank to keep)" : "Password"}
              type="password"
              name="password"
              value={form.password}
              onchange={handleChange}
              placeHolder={isEdit ? "••••••••" : "Set password"}
            />
          </div>
          <div className="col-md-6">
            <FormInput label="CNIC number" type="text" name="cnicNumber" value={form.cnicNumber} onchange={handleChange} placeHolder="12345-1234567-1" />
          </div>
        </div>
      </div>

      <div className="p-3 mb-3" style={sectionCardStyle}>
        {renderSectionTitle("Employment & pay")}
        <div className="row g-3">
          <div className="col-md-6">
            <label className="form-label">Type</label>
            <Select
              value={employmentOption}
              onChange={(opt) =>
                setForm((prev) => ({
                  ...prev,
                  employmentType: opt?.value || "Freelancer",
                  salary: opt?.value === "Salaried" ? prev.salary : 0,
                  hoursShift: opt?.value === "Salaried" ? (prev.hoursShift || 8) : 8,
                }))
              }
              options={EMPLOYMENT_OPTIONS}
              styles={selectStyles}
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
          </div>
          <div className="col-md-6">
            <FormInput
              label="Per order commission"
              type="number"
              name="perOrderCommission"
              value={form.perOrderCommission}
              onchange={handleChange}
              step="0.01"
            />
          </div>
          <div className="col-md-6">
            <label className="form-label">Star level</label>
            <Select
              value={starOption}
              onChange={(opt) => setForm((prev) => ({ ...prev, starLevel: opt?.value || 1 }))}
              options={STAR_LEVEL_OPTIONS}
              styles={selectStyles}
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
          </div>
          {isSalaried && (
            <>
              <div className="col-md-6">
                <label className="form-label">Hours shift</label>
                <Select
                  value={hoursOption}
                  onChange={(opt) => setForm((prev) => ({ ...prev, hoursShift: opt?.value || 8 }))}
                  options={HOURS_SHIFT_OPTIONS}
                  styles={selectStyles}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                />
              </div>
              <div className="col-md-6">
                <FormInput
                  label="Salary"
                  type="number"
                  name="salary"
                  value={form.salary}
                  onchange={handleChange}
                  step="0.01"
                />
              </div>
            </>
          )}
        </div>
      </div>

      <div className="p-3 mb-3" style={sectionCardStyle}>
        {renderSectionTitle("Documents")}
        <p className="small text-muted mb-3">Upload identity and vehicle documents. Previews appear after upload.</p>
        <div className="row g-3">
          {DOCUMENT_FIELDS.map((doc) => {
            const path = form[doc.key];
            const isUploading = uploading === doc.documentType;
            return (
              <div className="col-md-6 col-xl-4" key={doc.key}>
                <div className="p-3 h-100" style={docCardStyle}>
                  <div className="fw-semibold mb-2" style={{ fontSize: "0.9rem" }}>{doc.label}</div>
                  <div
                    className="d-flex align-items-center justify-content-center mb-2 rounded"
                    style={{
                      height: 96,
                      background: isDarkMode ? "rgba(0,0,0,0.25)" : "rgba(0,0,0,0.04)",
                      overflow: "hidden",
                    }}
                  >
                    {path ? (
                      <a href={toImageUrl(path)} target="_blank" rel="noopener noreferrer">
                        <img
                          src={toImageUrl(path)}
                          alt={doc.label}
                          style={{ maxHeight: 96, maxWidth: "100%", objectFit: "cover" }}
                        />
                      </a>
                    ) : (
                      <span className="text-muted small">
                        <i className="fa fa-image me-1" /> No file
                      </span>
                    )}
                  </div>
                  <input
                    type="file"
                    className="form-control form-control-sm"
                    accept="image/*"
                    disabled={isUploading || submitting}
                    onChange={(e) => handleImageUpload(e, doc.documentType, doc.key)}
                    style={{
                      borderRadius: "8px",
                      background: isDarkMode ? "#1e2425" : "#fff",
                      border: isDarkMode ? "1px solid #404040" : "1px solid #D9D9D9",
                      color: isDarkMode ? "#fff" : "#495057",
                    }}
                  />
                  {isUploading && <small className="text-muted d-block mt-1">Uploading…</small>}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="p-3 mb-3" style={sectionCardStyle}>
        {renderSectionTitle("Vehicle & service area")}
        <div className="row g-3">
          <div className="col-md-6">
            <FormInput label="Vehicle type" type="text" name="vehicleType" value={form.vehicleType} onchange={handleChange} placeHolder="Bike, car, etc." />
          </div>
          <div className="col-md-6">
            <FormInput label="Vehicle number" type="text" name="vehicleNumber" value={form.vehicleNumber} onchange={handleChange} placeHolder="Registration number" />
          </div>
          <CountryCitySelector
            selectedCountry={form.serviceCountryCode}
            selectedCity={form.serviceCity}
            setSelectedCity={(city) =>
              setForm((prev) => ({ ...prev, serviceCity: city, residenceZoneId: null }))
            }
            onChange={(payload) => {
              setForm((prev) => {
                const countryChanged = payload.CompCountry !== undefined && payload.CompCountry !== prev.serviceCountryCode;
                const cityChanged = payload.CompCity !== undefined && payload.CompCity !== prev.serviceCity;
                return {
                  ...prev,
                  serviceCountryCode: payload.CompCountry !== undefined ? (payload.CompCountry || "PAK") : prev.serviceCountryCode,
                  serviceCity: payload.CompCity !== undefined ? (payload.CompCity || "") : prev.serviceCity,
                  residenceZoneId: countryChanged || cityChanged ? null : prev.residenceZoneId,
                };
              });
              if (payload.CompCountry !== undefined || payload.CompCity !== undefined) {
                setSelectedResidenceZone(null);
              }
            }}
            selectedValueIsLabel={true}
            isDarkMode={isDarkMode}
          />
          <div className="col-12">
            <label className="form-label">Residence zone</label>
            <Select
              isClearable
              value={selectedResidenceZone}
              onChange={(opt) => {
                setSelectedResidenceZone(opt || null);
                setForm((prev) => ({ ...prev, residenceZoneId: opt?.value || null }));
              }}
              options={zoneOptions}
              styles={selectStyles}
              placeholder={form.serviceCity ? "Select residence zone" : "Select city first"}
              isDisabled={!form.serviceCity}
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
            <small className="text-muted d-block mt-1">
              Residence zone is for information only. Riders can book shifts in any zone.
            </small>
          </div>
          <div className="col-md-6">
            <FormInput label="Max radius (km)" type="number" name="maxRadiusKm" value={form.maxRadiusKm} onchange={handleChange} />
          </div>
          <div className="col-md-6">
            <label className="form-label">Approval status</label>
            <Select
              value={approvalOption}
              onChange={(opt) => setForm((prev) => ({ ...prev, approvalStatus: opt?.value || "Pending" }))}
              options={APPROVAL_OPTIONS}
              styles={selectStyles}
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
          </div>
          <div className="col-md-6 d-flex align-items-end pb-1">
            <ToggleSwitch
              isChecked={form.isOnline}
              onToggle={handleChange}
              label="Online"
              name="isOnline"
            />
          </div>
        </div>
      </div>

      <div className="d-flex justify-content-end gap-2 pt-2">
        <button type="button" className="btn" onClick={onCancel} disabled={busy} style={cancelBtnStyle}>
          Cancel
        </button>
        <button type="button" className="btn" onClick={handleSave} disabled={busy} style={primaryBtnStyle}>
          {submitting ? "Saving..." : isEdit ? "Update Rider" : "Create Rider"}
        </button>
      </div>
    </div>
  );
};

export default MarketplaceRiderForm;
